import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, Observable, from, throwError } from 'rxjs';
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthService } from '../services/auth.service';
import { SKIP_REFRESH_ON_401 } from './skip-refresh.context';

let isRefreshing = false;
const refreshDone = new BehaviorSubject<boolean>(false);

function withAuthHeader(
  request: HttpRequest<unknown>,
  authService: AuthService
): HttpRequest<unknown> {
  const accessToken = authService.getAccessToken();
  const headers =
    !accessToken || request.headers.has('Authorization')
      ? request.headers
      : request.headers.set('Authorization', `Bearer ${accessToken}`);

  return request.clone({ headers, withCredentials: true });
}

function handle401(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService
): Observable<HttpEvent<unknown>> {
  if (authService.isE2eToken()) {
    return throwError(() => new HttpErrorResponse({ status: 401 }));
  }

  if (isRefreshing) {
    return refreshDone.pipe(
      filter(done => done),
      take(1),
      switchMap(() => next(withAuthHeader(request, authService)))
    );
  }

  isRefreshing = true;
  refreshDone.next(false);

  return from(authService.refresh()).pipe(
    switchMap(() => next(withAuthHeader(request, authService))),
    catchError((error: unknown) => {
      authService.logout();
      return throwError(() => error);
    }),
    finalize(() => {
      isRefreshing = false;
      refreshDone.next(true);
    })
  );
}

function isOwnApiRequest(request: HttpRequest<unknown>): boolean {
  try {
    return (
      new URL(request.url, document.baseURI).origin ===
      new URL(environment.apiURl).origin
    );
  } catch {
    return false;
  }
}

export const authInterceptor = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  if (!isOwnApiRequest(request)) {
    return next(request);
  }

  const authService = inject(AuthService);

  return next(withAuthHeader(request, authService)).pipe(
    catchError((error: unknown) => {
      const httpError = error as HttpErrorResponse;
      if (
        httpError.status === 401 &&
        !request.context.get(SKIP_REFRESH_ON_401)
      ) {
        return handle401(request, next, authService);
      }
      return throwError(() => httpError);
    })
  );
};
